
import { FC, HTMLAttributes, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { GarAddressType, GarHintType } from "../../Types";
import { useInputHandler, useOutsideClick, useSearchMark } from "src/Hooks";
import { getFullOffsetBy, getScrollContainer } from "src/Utils";
import api from "../../API";
import st from "./Gar.module.scss";

interface IGarInputProps extends HTMLAttributes<HTMLDivElement> {

	name: string;
	initTextValue?: string;

	value?: GarAddressType;
	onChangeAddress?: ( address: GarAddressType | null ) => void;

	placeholder?: string;
	disabled?: boolean;

	sendOnlyChange?: boolean;

	isInvalidate?: boolean;
};

/** Тип позиции ( top, left ) */
type PositionType = {
	top: number;
	left: number;
	width: number;
} | null;

const GarInput: FC<IGarInputProps> = ( props ) => {

	const { 
		initTextValue = '', value, onChangeAddress, placeholder, disabled, isInvalidate = false, 
		sendOnlyChange = true, className, ...otherProps 
	} = props;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const controllerRef = useRef<AbortController|null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const modalRef = useOutsideClick<HTMLDivElement>( () => setIsOpen(false), "mouseup");
	const [ inputProps, setSearchText ] = useInputHandler( initTextValue, onChange, 500 );
	// Для подсветки найденных значений
	const { getHtml } = useSearchMark( st.mark );

	const [ isOpen, setIsOpen ] = useState(false);
	const [ position, setPosition ] = useState<PositionType>(null);
	const [ isWait, setIsWait ] = useState(false);
	const [ options, setOptions ] = useState<Array<GarHintType>>([]);
	const [ msg, setMsg ] = useState<string>("");
	const [ currentId, setCurrentId ] = useState<string>('');

	useLayoutEffect( () => {
		const wr = wrapperRef.current;
		const onClose = () => setIsOpen(false);
		if ( isOpen ) {
			const scrollContainer = getScrollContainer( wr );
			if ( !!scrollContainer ) {
				scrollContainer.addEventListener( "scroll", onClose );
			}
		}
		calcOffsetModalPosition();
		return () => {
			if ( !wr ) return;
			const scrollContainer = getScrollContainer( wr );
			if ( !!scrollContainer ) {
				scrollContainer.removeEventListener( "scroll", onClose)
			}
		};
	// eslint-disable-next-line
	}, [ isOpen ] );

	useEffect( () => {
		if ( isOpen && !!inputProps.value && !options.length ) {
			getHints( inputProps.value );
		}
	}, [ isOpen ] );

	useEffect( () => {
		if ( isOpen ) {
			calcOffsetModalPosition();
		}
	}, [ isOpen, options, isWait, msg ] );

	const calcOffsetModalPosition = () => {
		const wr = wrapperRef.current;
		if ( !!wr ) {
			const offset = getFullOffsetBy( wr, "rdc");
			let top = ( offset.top - offset.scrollTop ) + wr.offsetHeight;
			let left = ( offset.left - offset.scroll );
			const height = modalRef.current?.clientHeight || 430;

			if ( ( top + ( height + 50 ) ) > window.innerHeight ) {
				wr.classList.add( st.upper );
				modalRef.current?.classList.add( st.upper );
				top -= ( height + offset.height );
			}
			else if ( modalRef.current?.classList.contains( st.upper ) ) {
				modalRef.current?.classList.remove( st.upper );
				wr.classList.remove( st.upper );
			}

			if ( (left + wr.clientWidth) > window.innerWidth ) {
				left -= wr.clientWidth - 10;
			}
			setPosition( { top, left, width: offset.width } );
		}
	};

	/** Запрос на получение подсказок */
	const getHints = ( search: string ) => {
		if ( !!controllerRef.current ) {
			controllerRef.current.abort( "Убираем дублирование запросов" );
			controllerRef.current = null;
		}
		setIsWait(true);
		setMsg( "Загрузка..." );
		controllerRef.current = new AbortController();
		api.getHints( search, controllerRef.current )
			.then( res => {
				if ( res.hasError || !res.result ) {

					console.groupCollapsed( "ГАР" );
					console.warn( "Ошибка получения подсказок ГАР сервиса" );
					console.warn( res.errorMessage, res );
					console.groupEnd();

					setMsg( "Совпадений не найдено" );
					setOptions([]);
					return;
				}
				setMsg( !res.result.length ? "Совпадений не найдено" : "");
				setOptions( res.result );
			})
			.finally( () => setIsWait(false) );
	};

	/** Получаем данные адреса */
	const getAddress = ( item: GarHintType ) => {
		if ( !!controllerRef.current ) {
			controllerRef.current.abort( "Убираем дублирование запросов" );
			controllerRef.current = null;
		}
		setIsWait(true);
		const id = item.id.substring(item.id.lastIndexOf('/') + 1);
		controllerRef.current = new AbortController();
		api.getAddress( id, controllerRef.current )
			.then( res => {
				if ( res.hasError ) {
					setMsg( res.errorMessage || "" );
					return;
				}
				setCurrentId( item.id );
				setSearchText( item.text );
				setMsg("");
				if ( !!onChangeAddress && res.result ) {
					onChangeAddress( res.result );
				}
			})
			.finally( () => setIsWait(false) );
	};

	/** Вызывается при выборе подсказки */
	const onChangeHandler = ( item: GarHintType ) => {
		setIsOpen(false);
		getAddress( item );
	};

	/** Вызывается при очистке поля поиска */
	const onClearHandler = () => {
		if ( controllerRef.current ) {
			controllerRef.current.abort();
		}
		setSearchText("");
		if ( !!onChangeAddress )
			onChangeAddress(null);
		if( isOpen ) setIsOpen(false);
		setMsg( "Введите минимум 3 символа" );
		setCurrentId('');
		setOptions([]);
		setIsWait(false);
		if ( !!inputRef.current ) {
			inputRef.current.focus();
		}
	};

	/** Вызывается при вводе в поле поиска */
	function onChange ( searchValue: string ) {
		if ( disabled ) return;

		if ( !searchValue ) {
			if ( isOpen ) 
				setIsOpen(false);
			return;
		}
		else if ( !isOpen ) 
			setIsOpen(true);

		if ( searchValue.length < 3 ) {
			setMsg( "Введите минимум 3 символа" );
			setOptions([]);
			return;
		}
		getHints( searchValue );
	};

	/** Вызывается при клике на поле поиска */
	const onClickInputHandler = () => {
		if ( !inputProps.value ) return;
		if ( sendOnlyChange && !isOpen ) {
			setIsOpen( true );
		}
		else if ( !sendOnlyChange ) {
			onChange( inputProps.value );
		}
	};

	/** Выбранный ли элемент */
	const getIsChecked = ( item: GarHintType ): boolean => {
		return !!currentId && currentId === item.id;
	};
	
	/** Отрисовка элементов выпадающего списка */
	const renderOption = ( item: GarHintType, index: number ) => {
		const isChecked = getIsChecked(item);
		const itemClassses = cn({ [st.active]: isChecked });
		return (
			<li key={index} className={itemClassses} id={item.id} onClick={ () => onChangeHandler(item)}>
				<p 
					title={item.text}
					dangerouslySetInnerHTML={{ __html: getHtml( item.text, inputProps.value ) }}
				/>
			</li>
		);
	};

	const renderList = () => {
		const msgClasses = cn( st.garModal__message );
		const isUpper = modalRef.current?.classList.contains( st.upper );
		return (
			<div ref={modalRef} className={st.garModal} style={{ ...position }}>
				{ ( !isUpper && !!msg ) && <p className={msgClasses}>{msg}</p> }
				<ul className={st.garModal__List}>
					{ !!options?.length && options.map( renderOption )}
				</ul>
				{ ( isUpper && !!msg ) && <p className={msgClasses}>{msg}</p> }
			</div>
		);
	};

	const renderClearBtn = () => {
		if ( disabled || !inputProps.value ) return null;
		return (
			<button type="button" onClick={onClearHandler}>
				<svg width="32" height="32" viewBox="0 0 32 32" fill="inherit" xmlns="http://www.w3.org/2000/svg">
					<path d="M20.4964 12.5107C20.7745 12.2326 20.7745 11.7817 20.4964 11.5036C20.2183 11.2255 19.7674 11.2255 19.4893 11.5036L16 14.9929L12.5107 11.5036C12.2326 11.2255 11.7817 11.2255 11.5036 11.5036C11.2255 11.7817 11.2255 12.2326 11.5036 12.5107L14.9929 16L11.5036 19.4893C11.2255 19.7674 11.2255 20.2183 11.5036 20.4964C11.7817 20.7745 12.2326 20.7745 12.5107 20.4964L16 17.0071L19.4893 20.4964C19.7674 20.7745 20.2183 20.7745 20.4964 20.4964C20.7745 20.2183 20.7745 19.7674 20.4964 19.4893L17.0071 16L20.4964 12.5107Z" fill="inherit"/>
				</svg>
			</button>
		);
	};

	const wrapperClasses = cn( st.gar, className, {
		[st.opened]: isOpen,
		[st.disabled]: disabled,
		[st.isInvalidate]: isInvalidate && !isOpen
	}); 
	return (
		<div ref={wrapperRef} className={wrapperClasses} onClick={onClickInputHandler} {...otherProps}>
			<svg width="32" height="32" viewBox="0 0 32 32" fill="inherit" xmlns="http://www.w3.org/2000/svg">
				<path d="M11 13.5L16 18.5L21 13.5H11Z" fill="inherit"/>
			</svg>
			<input
				ref={inputRef}
				type="text"
				title={inputProps.value}
				placeholder={placeholder}
				autoComplete="off"
				className={st.input}
				disabled={disabled}
				{...inputProps}
			/>
			<div className={st.gar__toolbar}>
				{ isWait && <div className={st.gar_spinner} /> }
				{ renderClearBtn() }
			</div>
			{ isOpen && createPortal( renderList(), document.body) }
		</div>
	);
};

export default GarInput;
